import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  TruckIcon,
  ClockIcon,
  PlusCircleIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  FolderOpenIcon,
  DocumentMagnifyingGlassIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';

const actions = [
  {
    title: 'Manage Vehicle',
    to: '/vehicles/manage',
    desc: "Register Customer & Vehicle, View History, Create Job",
    component: Link,
    icon: TruckIcon,
    category: 'vehicle',
    gradient: 'from-purple-500 to-indigo-600',
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-100',
  },
  {
    title: 'Manage Open Jobs',
    to: '/vehicles/openjobs',
    desc: "Update the Status of Open Job Cards",
    icon: WrenchScrewdriverIcon,
    category: 'job',
    gradient: 'from-blue-500 to-cyan-600',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
  {
    title: 'Diagnostic Questionnaires',
    to: '/vehicles/diagq',
    desc: "Update the Status of DQs",
    icon: ClipboardDocumentCheckIcon,
    category: 'job',
    gradient: 'from-blue-500 to-cyan-600',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
  {
    title: 'Closed Jobs',
    to: '/vehicles/closedjobs',
    desc: "View Closed Job Cards",
    icon: CheckCircleIcon,
    category: 'job',
    gradient: 'from-blue-500 to-cyan-600',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
  {
    title: 'All Jobs',
    to: '/vehicles/alljobs',
    desc: "View All Jobs - Active and Closed",
    icon: FolderOpenIcon,
    category: 'job',
    gradient: 'from-blue-500 to-cyan-600',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
  {
    title: 'Scheduled Appointments',
    to: '/vehicles/appointments',
    icon: CalendarDaysIcon,
    component: Link,
    desc: "View Appointment",
    category: 'task',
    gradient: 'from-emerald-500 to-teal-600',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
  },
  {
    title: 'Task Scheduler',
    to: '/vehicles/assigntask',
    icon: PlusCircleIcon,
    component: Link,
    desc: "Assign Task to Mechanic",
    category: 'task',
    gradient: 'from-emerald-500 to-teal-600',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
  },
  {
    title: 'Mechanic Performance',
    to: '/vehicles/mechanicperformance',
    icon: ChartBarIcon,
    component: Link,
    desc: "Analytics on Job Cards and Engineers",
    category: 'analytics',
    gradient: 'from-rose-500 to-pink-600',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
  },
  {
    title: 'Manager Task Monitoring',
    to: '/vehicles/managertasks',
    icon: DocumentMagnifyingGlassIcon,
    component: Link,
    desc: "Manager Tracking and Updates on Tasks",
    category: 'analytics',
    gradient: 'from-rose-500 to-pink-600',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
  },
  {
    title: 'Task Dashboard',
    to: '/vehicles/taskdashboard',
    icon: ChartBarIcon,
    component: Link,
    desc: "View New, In-Progress & Closed Count",
    category: 'analytics',
    gradient: 'from-rose-500 to-pink-600',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
  },
  {
    title: 'Task Completion Report',
    to: '/vehicles/taskcompleted',
    icon: DocumentMagnifyingGlassIcon,
    component: Link,
    desc: "Analytics on Completed Tasks",
    category: 'analytics',
    gradient: 'from-rose-500 to-pink-600',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
  },
  {
    title: 'Mechanic Task Calendar',
    to: '/vehicles/mechanic-calendar',
    icon: ClockIcon,
    component: Link,
    desc: "Analytics on Completed Tasks",
    category: 'analytics',
    gradient: 'from-rose-500 to-pink-600',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
  },
]

const pages = [
    { name: 'Service Advisor', to: '/vehicles', component: Link, current: true },
     ];

const Vehicle = () => {
  const { role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-lg font-semibold text-gray-700">Loading Service Advisor...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <BreadCrumb pages={pages} />

      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-gray-900 mb-3">
            Service Advisor Dashboard
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Manage vehicles, job cards, tasks, and performance analytics from one central hub
          </p>
        </div>

        {/* Modern Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {actions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-200 overflow-hidden transform hover:-translate-y-1"
            >
              {/* Gradient Background Accent */}
              <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${action.gradient}`}></div>

              <div className="p-6">
                {/* Icon */}
                <div className="flex items-center justify-center mb-4">
                  <div className={`${action.iconBg} p-4 rounded-2xl shadow-sm ring-4 ring-white group-hover:scale-110 transition-transform duration-200`}>
                    <action.icon className={`h-8 w-8 ${action.iconColor}`} aria-hidden="true" />
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors duration-200">
                    {action.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {action.desc}
                  </p>
                </div>

                {/* Arrow Icon */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <svg
                    className={`h-6 w-6 ${action.iconColor}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Vehicle